import React, { FC, useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import Transition from 'react-transition-group/Transition';

import { animationSpeed, CardAnimateur } from '~src/modules/questions/containers/stream.sc';
import CardNavigation from '~src/modules/questions/components/CardNavigation';
import { NavigationContainer } from '~src/modules/questions/components/shared.sc';
import { AnswerValue, CardI } from '~src/interfaces/question.interface';
import { useNotification } from '~src/services/hooks/useNotification';

export type AnimateContent = (
  next: () => void,
  prev: () => void,
  currentIndex: number,
) => React.ReactNode | null;

interface Props {
  max: number;
  onFinish: () => void;
  startIndex: number;
  children: AnimateContent;
  renderFooter?: AnimateContent;
  onLeave?: (index: number) => Promise<unknown> | void;
  cards?: CardI[];
  onSubmit?: (values: AnswerValue, currentCard: CardI) => Promise<void>;
  onChangeComment?: (comment: string) => void;
}

const StreamAnimation: FC<Props> = (props) => {
  const [direction, setDirection] = useState('left');
  const [currentIndex, setCurrentIndex] = useState<number>(props.startIndex);
  const [isVisible, setIsVisible] = useState(true);
  const { notificationsFromAPIErrors } = useNotification();

  useEffect(() => {
    setCurrentIndex(props.startIndex);
  }, [props.startIndex]);

  useEffect(() => {
    if (
      currentIndex !== undefined &&
      props.onChangeComment &&
      props.cards &&
      props.cards[currentIndex] &&
      props.cards[currentIndex].answer &&
      props.cards[currentIndex].answer.comment
    ) {
      props.onChangeComment(
        props.cards[currentIndex].answer ? (props.cards[currentIndex].answer.comment as string) || '' : '',
      );
    }
  }, [currentIndex]);

  const moveCard = (moveDirection: 'left' | 'right', callback: () => void) => {
    const execMove = () => {
      // To trigger the animation twice (entering/leaving) we need to call the animation again
      // and wait for the first one to be finished
      setDirection(moveDirection);
      setIsVisible(false);
      setTimeout(() => {
        callback();
        setIsVisible(true);
      }, animationSpeed);
    };
    if (props.onLeave) {
      const rsl = props.onLeave(currentIndex);
      if (rsl instanceof Promise) {
        rsl
          .then(() => {
            execMove();
          })
          .catch((err) => {
            notificationsFromAPIErrors(err, false, 'x.error');
          });
        return;
      }
    }
    execMove();
  };

  const next = () => {
    if (currentIndex >= props.max - 1) {
      setIsVisible(false);
      if (props.onLeave) {
        const leaveRsl = props.onLeave(currentIndex);
        if (leaveRsl instanceof Promise) {
          props.onFinish();

          return;
        }
      }
      props.onFinish();
    } else {
      moveCard('left', () => {
        setCurrentIndex(currentIndex + 1);
      });
    }
  };

  const prev = () => {
    if (currentIndex > 0) {
      moveCard('right', () => {
        setCurrentIndex(currentIndex - 1);
      });
    }
  };

  const swipeableHandlers = useSwipeable({
    onSwipedRight: prev,
    onSwipedLeft: next,
  });

  return (
    <>
      <Transition in={isVisible} timeout={animationSpeed}>
        {(status) => (
          <CardAnimateur $direction={direction} className={status}>
            <div {...swipeableHandlers} >
              {props.children(next, prev, currentIndex)}
            </div>
          </CardAnimateur>
        )}
      </Transition>
      <NavigationContainer>
        <CardNavigation
          card={props.cards ? props.cards[currentIndex] : undefined}
          next={next}
          back={prev}
          max={props.max}
          value={currentIndex + 1}
          onSubmit={props.onSubmit}
        />
      </NavigationContainer>
      {props.renderFooter && props.renderFooter(next, prev, currentIndex)}
    </>
  );
};

export default StreamAnimation;
